import {
    navigateToAccountPage,
    useThirdPartyBalancesPageQuery,
} from '../../../../services';
import { useNavigate } from 'react-router-dom';
import { DPage, DPageLoader } from '../../../dum';
import {
    formatDate,
    formatFloat,
    handleTouchDoubleTap,
    isInteractiveTarget,
    TouchDoubleTapState,
} from '../../../../helpers';
import React, { useMemo, useRef, useState } from 'react';
import {
    Box,
    Checkbox,
    FormControlLabel,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    useTheme,
} from '@mui/material';
import {
    CheckBox,
    CheckBoxOutlineBlank,
    EditNoteOutlined,
    InfoOutlined,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import {
    SUpdateExpenseDetailsDialog,
    SUpsertExpenseDialog,
} from '../../../smart';

export function ThirdPartyBalances() {
    const theme = useTheme();
    const alternateColor = theme.palette.mode === 'dark' ? '#303030' : '#E0E0E0';
    const hoverColor = theme.palette.mode === 'dark' ? '#4a4a4a' : '#BDBDBD';
    const { data, refetch, loading } = useThirdPartyBalancesPageQuery();
    const navigate = useNavigate();
    const [upsertOpen, setUpsertOpen] = useState(false);
    const [expenseId, setExpenseId] = useState<number | null>(null);
    // When on, each expense with MORE THAN ONE resource expands into one row per
    // expense_resource (name, units, unit price) beneath it. A single-resource
    // expense is left as its summed row — breaking it down would just repeat the
    // same numbers. Off by default.
    const [breakdownResources, setBreakdownResources] = useState(false);
    // Off by default the page is Nota-only (receipt_type_id = 1). When on, every
    // receipt type is included, so the balance covers ALL of the account's
    // expenses, not just Notas.
    const [includeAllExpenses, setIncludeAllExpenses] = useState(false);
    // Optional-details edit (folio, payment date, ...) — the lightweight
    // edit that is not subject to the full upsert's status lock. Double-clicking
    // a row still opens the full upsert.
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsExpenseId, setDetailsExpenseId] = useState<number | null>(
        null,
    );
    const touchDoubleTap = useRef<TouchDoubleTapState | null>(null);

    const handleUpsertOpenChange = (open: boolean) => {
        setUpsertOpen(open);
        if (!open) {
            void refetch();
        }
    };

    // The Proveedor cell now navigates to that account's page rather than opening
    // its upsert dialog — the balances view is a read-oriented drill-down, so a
    // click on the account should take you to the account, not into an edit form.
    const goToAccountPage = (accountId: number | null | undefined) => {
        if (accountId == null) {
            return;
        }
        navigateToAccountPage({ navigate, accountId });
    };

    const balancesWithExpenses = useMemo(() => {
        if (!data) {
            return [];
        }

        const balances = [...data.getThirdPartyAccountsTransferSummary];
        const receiptTypeNameById = new Map(
            data.getReceiptTypes.map((receiptType) => [
                receiptType.id,
                receiptType.name,
            ]),
        );
        const notaReceiptTypeIds = new Set(
            data.getReceiptTypes
                .filter((receiptType) => !receiptType.applies_tax)
                .map((receiptType) => receiptType.id),
        );

        const processedBalances = balances
            .map((b) => {
                return {
                    ...b,
                    children: data.getExpensesWithDisparities
                        .filter((expense) => {
                            // Default: only non-taxable "Nota" expenses,
                            // filtered client-side off the full response.
                            // With includeAllExpenses on, keep every receipt type.
                            return (
                                expense.account_id === b.account_id &&
                                (includeAllExpenses ||
                                    notaReceiptTypeIds.has(
                                        expense.receipt_type_id ?? 0,
                                    ))
                            );
                        })
                        .map((exp) => {
                            return {
                                ...exp,
                                receipt_type_name:
                                    receiptTypeNameById.get(
                                        exp.receipt_type_id ?? 0,
                                    ) ?? '',
                            };
                        }),
                };
            })
            // The whole page is Nota-scoped, so visibility must be Nota-only
            // too. The server's expenses_total / transfer_receipts_total
            // aggregate ALL receipt types (Facturas included), so we can't use
            // them here — we derive the outstanding balance from the Nota
            // children instead. Keep an account only when it has Nota rows and
            // those rows still carry a non-zero balance against their
            // transfers.
            .filter((b) => {
                if (b.children.length === 0) {
                    return false;
                }
                const notaBalance = b.children.reduce(
                    (acc, curr) =>
                        acc +
                        curr.total_with_tax -
                        curr.transfer_receipts_total,
                    0,
                );
                // Round to cents before comparing to avoid float dust leaving
                // fully-settled accounts on screen.
                return Math.round(notaBalance * 100) !== 0;
            });

        processedBalances.sort((a, b) => {
            return (a.account?.compound_name || '').localeCompare(
                b.account?.compound_name || '',
            );
        });

        processedBalances.forEach((item) => {
            item.children.sort((a, b) => {
                if (a.date && b.date) {
                    const aDayjs = dayjs(a.date);
                    const bDayjs = dayjs(b.date);
                    return aDayjs.isBefore(bDayjs)
                        ? -1
                        : aDayjs.isAfter(bDayjs)
                        ? 1
                        : 0;
                } else if (a.date) {
                    return -1;
                } else {
                    return 1;
                }
            });
        });

        return processedBalances;
    }, [data, includeAllExpenses]);

    if (loading && !data) {
        return <DPageLoader />;
    }

    return (
        <DPage>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2, mb: 1 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={breakdownResources}
                            onChange={() =>
                                setBreakdownResources((prev) => !prev)
                            }
                        />
                    }
                    label={'Desglosar recursos'}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={includeAllExpenses}
                            onChange={() =>
                                setIncludeAllExpenses((prev) => !prev)
                            }
                        />
                    }
                    label={'Incluir todos los gastos'}
                />
            </Box>

            <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <Table size={'small'} padding={'checkbox'} stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ width: '20%' }}>Proveedor</TableCell>
                            <TableCell style={{ width: '1%', whiteSpace: 'nowrap' }}>
                                Fecha del gasto
                            </TableCell>
                            <TableCell>Tipo de recibo</TableCell>
                            <TableCell align={'right'}>Unidades</TableCell>
                            <TableCell align={'right'}>
                                Precio unitario (con IVA)
                            </TableCell>
                            <TableCell align={'right'}>
                                Subtotal (sin IVA)
                            </TableCell>
                            <TableCell align={'right'}>Total con IVA</TableCell>
                            <TableCell align={'right'}>
                                Total transferido
                            </TableCell>
                            <TableCell align={'right'}>
                                Saldo pendiente
                            </TableCell>
                            <TableCell align={'center'} style={{ width: 36 }}>
                                {' '}
                            </TableCell>
                            <TableCell align={'center'} style={{ width: 36 }}>
                                {' '}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {balancesWithExpenses.map(
                            (balanceWithExpenses, balanceI) => {
                                return (
                                    <React.Fragment
                                        key={balanceWithExpenses.account_id}
                                    >
                                        {balanceWithExpenses.children.map(
                                            (expense, childrenI) => {
                                                return (
                                                    <React.Fragment
                                                        key={expense.id}
                                                    >
                                                        <TableRow
                                                            onDoubleClick={(e) => {
                                                                if (
                                                                    e.currentTarget instanceof
                                                                        HTMLElement &&
                                                                    isInteractiveTarget(
                                                                        e,
                                                                        e.currentTarget,
                                                                    )
                                                                )
                                                                    return;
                                                                setExpenseId(
                                                                    expense.id,
                                                                );
                                                                setUpsertOpen(
                                                                    true,
                                                                );
                                                            }}
                                                            onTouchEnd={(e) => {
                                                                handleTouchDoubleTap(
                                                                    e,
                                                                    touchDoubleTap,
                                                                    () => {
                                                                        setExpenseId(
                                                                            expense.id,
                                                                        );
                                                                        setUpsertOpen(
                                                                            true,
                                                                        );
                                                                    },
                                                                    `expense-${expense.id}`,
                                                                );
                                                            }}
                                                            title="Doble clic o doble toque para abrir"
                                                            sx={{
                                                                cursor: 'pointer',
                                                                backgroundColor:
                                                                    balanceI %
                                                                        2 ===
                                                                    0
                                                                        ? alternateColor
                                                                        : undefined,
                                                                px: 1,
                                                                touchAction:
                                                                    'manipulation',
                                                                '&:hover': {
                                                                    backgroundColor: hoverColor,
                                                                },
                                                            }}
                                                        >
                                                            {childrenI === 0 ? (
                                                                <TableCell
                                                                    onDoubleClick={(
                                                                        e,
                                                                    ) => {
                                                                        e.stopPropagation();
                                                                        goToAccountPage(
                                                                            balanceWithExpenses.account_id,
                                                                        );
                                                                    }}
                                                                    onTouchEnd={(e) => {
                                                                        e.stopPropagation();
                                                                        handleTouchDoubleTap(
                                                                            e,
                                                                            touchDoubleTap,
                                                                            () => {
                                                                                goToAccountPage(
                                                                                    balanceWithExpenses.account_id,
                                                                                );
                                                                            },
                                                                            `account-${balanceWithExpenses.account_id}`,
                                                                        );
                                                                    }}
                                                                    sx={{
                                                                        textAlign:
                                                                            'left',
                                                                        fontWeight:
                                                                            'bold',
                                                                        cursor: 'pointer',
                                                                        touchAction:
                                                                            'manipulation',
                                                                        backgroundColor:
                                                                            balanceI % 2 === 0
                                                                                ? alternateColor
                                                                                : 'background.paper',
                                                                        '&:hover': {
                                                                            textDecoration:
                                                                                'underline',
                                                                        },
                                                                    }}
                                                                    rowSpan={
                                                                        // 1 totals row + each expense row, plus its
                                                                        // resource rows when broken down.
                                                                        1 +
                                                                        balanceWithExpenses.children.reduce(
                                                                            (
                                                                                acc,
                                                                                exp,
                                                                            ) =>
                                                                                acc +
                                                                                1 +
                                                                                (breakdownResources &&
                                                                                exp
                                                                                    .expense_resources
                                                                                    .length >
                                                                                    1
                                                                                    ? exp
                                                                                          .expense_resources
                                                                                          .length
                                                                                    : 0),
                                                                            0,
                                                                        )
                                                                    }
                                                                >
                                                                    {
                                                                        balanceWithExpenses
                                                                            .account
                                                                            ?.compound_name
                                                                    }
                                                                </TableCell>
                                                            ) : null}

                                                            <TableCell
                                                                sx={{
                                                                    textAlign:
                                                                        'left',
                                                                }}
                                                            >
                                                                {formatDate(
                                                                    expense.date,
                                                                )}
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    textAlign:
                                                                        'left',
                                                                }}
                                                            >
                                                                {
                                                                    expense.receipt_type_name
                                                                }
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    textAlign:
                                                                        'right',
                                                                }}
                                                            >
                                                                {formatFloat(
                                                                    expense.expense_resources.reduce(
                                                                        (
                                                                            acc,
                                                                            curr,
                                                                        ) => {
                                                                            return (
                                                                                acc +
                                                                                curr.units
                                                                            );
                                                                        },
                                                                        0,
                                                                    ),
                                                                )}
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    textAlign:
                                                                        'right',
                                                                }}
                                                            >
                                                                {expense.total_with_tax !==
                                                                0
                                                                    ? formatFloat(
                                                                          expense.total_with_tax /
                                                                              expense.expense_resources.reduce(
                                                                                  (
                                                                                      acc,
                                                                                      curr,
                                                                                  ) => {
                                                                                      return (
                                                                                          acc +
                                                                                          curr.units
                                                                                      );
                                                                                  },
                                                                                  0,
                                                                              ),
                                                                      )
                                                                    : ''}
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    textAlign:
                                                                        'right',
                                                                }}
                                                            >
                                                                {formatFloat(
                                                                    expense.subtotal,
                                                                )}
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    textAlign:
                                                                        'right',
                                                                }}
                                                            >
                                                                {formatFloat(
                                                                    expense.total_with_tax,
                                                                )}
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    textAlign:
                                                                        'right',
                                                                }}
                                                            >
                                                                {expense.transfer_receipts_total
                                                                    ? formatFloat(
                                                                          expense.transfer_receipts_total,
                                                                      )
                                                                    : ''}
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    textAlign:
                                                                        'right',
                                                                }}
                                                            >
                                                                {formatFloat(
                                                                    expense.total_with_tax -
                                                                        expense.transfer_receipts_total,
                                                                )}
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    textAlign:
                                                                        'center',
                                                                }}
                                                            >
                                                                <Tooltip
                                                                    arrow
                                                                    title={
                                                                        <Box>
                                                                            <Box>
                                                                                {expense.is_draft
                                                                                    ? 'El importe aún no está confirmado por el proveedor'
                                                                                    : 'El proveedor está de acuerdo con el importe'}
                                                                            </Box>
                                                                            {expense.notes ? (
                                                                                <Box
                                                                                    sx={{
                                                                                        mt: 1,
                                                                                    }}
                                                                                >
                                                                                    Notas: {expense.notes}
                                                                                </Box>
                                                                            ) : null}
                                                                        </Box>
                                                                    }
                                                                >
                                                                    <Box
                                                                        component={
                                                                            'span'
                                                                        }
                                                                        onClick={(
                                                                            e: React.MouseEvent<HTMLSpanElement>,
                                                                        ) =>
                                                                            e.stopPropagation()
                                                                        }
                                                                        sx={{
                                                                            display:
                                                                                'inline-flex',
                                                                            position:
                                                                                'relative',
                                                                            cursor: 'default',
                                                                        }}
                                                                    >
                                                                        {expense.is_draft ? (
                                                                            <CheckBoxOutlineBlank
                                                                                fontSize={
                                                                                    'small'
                                                                                }
                                                                                color={
                                                                                    'action'
                                                                                }
                                                                            />
                                                                        ) : (
                                                                            <CheckBox
                                                                                fontSize={
                                                                                    'small'
                                                                                }
                                                                                color={
                                                                                    'action'
                                                                                }
                                                                            />
                                                                        )}
                                                                        {expense.notes ? (
                                                                            <InfoOutlined
                                                                                sx={{
                                                                                    position:
                                                                                        'absolute',
                                                                                    top: -2,
                                                                                    right: -4,
                                                                                    fontSize: 10,
                                                                                    color: 'info.main',
                                                                                }}
                                                                            />
                                                                        ) : null}
                                                                    </Box>
                                                                </Tooltip>
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    textAlign:
                                                                        'center',
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        alignItems:
                                                                            'center',
                                                                        justifyContent:
                                                                            'center',
                                                                        gap: 1,
                                                                    }}
                                                                >
                                                                    {/* Optional-details edit. Full upsert is
                                                                        the row double-click. */}
                                                                    <Tooltip
                                                                        arrow
                                                                        title={
                                                                            'Editar datos opcionales'
                                                                        }
                                                                    >
                                                                        <IconButton
                                                                            size={
                                                                                'small'
                                                                            }
                                                                            onClick={(
                                                                                e,
                                                                            ) => {
                                                                                e.stopPropagation();
                                                                                setDetailsExpenseId(
                                                                                    expense.id,
                                                                                );
                                                                                setDetailsOpen(
                                                                                    true,
                                                                                );
                                                                            }}
                                                                        >
                                                                            <EditNoteOutlined
                                                                                fontSize={
                                                                                    'small'
                                                                                }
                                                                            />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                        {breakdownResources &&
                                                        expense.expense_resources
                                                            .length > 1
                                                            ? expense.expense_resources.map(
                                                                  (er) => (
                                                                      <TableRow
                                                                          key={`res-${er.id}`}
                                                                          sx={{
                                                                              backgroundColor:
                                                                                  balanceI %
                                                                                      2 ===
                                                                                  0
                                                                                      ? alternateColor
                                                                                      : undefined,
                                                                          }}
                                                                      >
                                                                          <TableCell
                                                                              sx={{
                                                                                  textAlign:
                                                                                      'left',
                                                                                  pl: 4,
                                                                                  color: 'text.secondary',
                                                                              }}
                                                                          >
                                                                              {er
                                                                                  .resource
                                                                                  ?.name ??
                                                                                  ''}
                                                                              {er.notes
                                                                                  ? ` (${er.notes})`
                                                                                  : ''}
                                                                          </TableCell>
                                                                          <TableCell />
                                                                          <TableCell
                                                                              sx={{
                                                                                  textAlign:
                                                                                      'right',
                                                                              }}
                                                                          >
                                                                              {formatFloat(
                                                                                  er.units,
                                                                              )}
                                                                          </TableCell>
                                                                          <TableCell
                                                                              sx={{
                                                                                  textAlign:
                                                                                      'right',
                                                                              }}
                                                                          >
                                                                              {formatFloat(
                                                                                  er.unit_price,
                                                                              )}
                                                                          </TableCell>
                                                                          <TableCell
                                                                              sx={{
                                                                                  textAlign:
                                                                                      'right',
                                                                              }}
                                                                          >
                                                                              {formatFloat(
                                                                                  (er.units ??
                                                                                      0) *
                                                                                      (er.unit_price ??
                                                                                          0),
                                                                              )}
                                                                          </TableCell>
                                                                          <TableCell />
                                                                          <TableCell />
                                                                          <TableCell />
                                                                          <TableCell />
                                                                          <TableCell />
                                                                      </TableRow>
                                                                  ),
                                                              )
                                                            : null}
                                                    </React.Fragment>
                                                );
                                            },
                                        )}
                                        <TableRow
                                            sx={{
                                                backgroundColor:
                                                    balanceI % 2 === 0
                                                        ? alternateColor
                                                        : undefined,
                                            }}
                                        >
                                            <TableCell />
                                            <TableCell />
                                            <TableCell />
                                            <TableCell />
                                            <TableCell />
                                            <TableCell
                                                sx={{
                                                    textAlign: 'right',
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {formatFloat(
                                                    balanceWithExpenses.children.reduce(
                                                        (acc, curr) => {
                                                            return (
                                                                acc +
                                                                curr.total_with_tax
                                                            );
                                                        },
                                                        0,
                                                    ),
                                                )}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    textAlign: 'right',
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {formatFloat(
                                                    balanceWithExpenses.children.reduce(
                                                        (acc, curr) => {
                                                            return (
                                                                acc +
                                                                curr.transfer_receipts_total
                                                            );
                                                        },
                                                        0,
                                                    ),
                                                )}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    textAlign: 'right',
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {formatFloat(
                                                    // Nota-only balance =
                                                    // Total − Transferido over
                                                    // the Nota children shown
                                                    // above. Facturas are
                                                    // excluded (the server
                                                    // expenses_total mixes both
                                                    // receipt types).
                                                    balanceWithExpenses.children.reduce(
                                                        (acc, curr) =>
                                                            acc +
                                                            curr.total_with_tax -
                                                            curr.transfer_receipts_total,
                                                        0,
                                                    ),
                                                )}
                                            </TableCell>
                                            <TableCell />
                                            <TableCell />
                                        </TableRow>
                                    </React.Fragment>
                                );
                            },
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <SUpsertExpenseDialog
                open={upsertOpen}
                setOpen={handleUpsertOpenChange}
                expenseId={expenseId}
            />
            <SUpdateExpenseDetailsDialog
                open={detailsOpen}
                setOpen={(open) => {
                    setDetailsOpen(open);
                    if (!open) {
                        setDetailsExpenseId(null);
                        void refetch();
                    }
                }}
                expenseId={detailsExpenseId}
            />
        </DPage>
    );
}
